import React from "react";
import { Dialog, DialogContent, DialogTitle, IconButton, Stack, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import GeneralStatusLabel from "../../components/Common/GeneralStatusLabel";
import CustomSelect from "../../components/Form/CustomSelect";
import CustomTextField from "../../components/Form/CustomTextField";
import CustomTimePicker from "../../components/Form/CustomTimePicker";
import DatePicker from "../../components/Form/DatePicker";
import Helpers from "../../helpers/helpers";
import { bookingId, patientName } from "./bookingPresentation";
import { displayCategory, displayStatus } from "./packagePresentation";
import {
  bookingDialogActions, bookingReasonOptions, cancellationReasonOptions, packageDeleteDialogAction,
} from "./bookingDialogModel";
import "../../components/Form/form-layout.css";
function SummaryCard({ title, subtitle, status, list }) {
  return <Stack
    direction="row" alignItems="center" justifyContent="space-between" gap={3}
    sx={{
      p: 2,
      border: "1px solid #1E2A331A",
      borderRadius: 1,
      bgcolor: "#f8fafc"
    }}
  >
    <Box className="text-break">
      <Box className="mb-1">{title}</Box>
      <Box className="text-muted">{subtitle}</Box>
    </Box>
    <GeneralStatusLabel label={status} list={list} />
  </Stack>;
}
export default function PackageDialog({
  content,
  setContent,
  values = {},
  onConfirm,
  setValues,
  loading = false,
  type,
  data = {},
  reschedule_reasons,
  reopening_reasons
}) {
  const actionType = type || content.type;
  const isDelete = actionType === "delete";
  const config = isDelete ? packageDeleteDialogAction : bookingDialogActions[actionType];
  const general = useSelector(state => state.General);
  const reasonLists = {
    reschedule: reschedule_reasons ?? general?.reschedule_reasons,
    reopen: reopening_reasons ?? general?.reopening_reasons
  };
  const reasons = actionType === "cancel"
    ? cancellationReasonOptions
    : reasonLists[actionType] ?? bookingReasonOptions;
  const reference = bookingId(data) || content.id;
  const name = patientName(data);
  const packageTitle = data.name || data.title || "this package";
  const packageType = Helpers.packageTypes.find(item =>
    item.value === String(data.type || data.package_type || "package").toLowerCase()
  )?.label.toLowerCase() || "package";
  const handleClose = () => {
    if (!loading) setContent({
      open: false
    });
  };
  const change = field => event => {
    setValues(current => ({
      ...current,
      [field]: event.target.value
    }));
  };
  const handleSubmit = event => {
    event.preventDefault();
    if (!loading) onConfirm?.(content.id ?? data.id, values);
  };
  const renderMessage = () => {
    switch (actionType) {
      case "delete":
        return <Stack spacing={3}>
          <SummaryCard
            title={packageTitle} subtitle={displayCategory(data.category || data.category_name)}
            status={displayStatus(data.status)} list={Helpers.packageVisibilityStatuses}
          />
          <Box>
            <p className="mb-2">Once deleted,</p>
            <ul className="text-muted mb-0 ps-4">
              <li className="mb-2">
                The {packageType === "package" ? "package" : `${packageType} package`} will
                be immediately removed from the public website.
              </li>
              <li>This action is permanent and cannot be undone.</li>
            </ul>
          </Box>
          <p className="mb-0">
            Are you sure you want to delete <strong>{packageTitle}</strong>?
          </p>
        </Stack>;
      case "confirm":
        return <>
          <p className="mb-4">
            You are about to confirm booking {reference} for
          </p>
          <Box className="mb-4">
            <SummaryCard title={reference} subtitle={name} status={data.status} list={Helpers.bookingStatuses} />
          </Box>
          <p className="mb-0">
            A confirmation notification will be sent to the assigned department email.
            The booking status will be updated to Confirmed.
          </p>
        </>;
      case "reschedule":
        return <p className="mb-0">Rescheduling booking <strong>{reference}</strong> for:</p>;
      case "complete":
        return <p className="mb-0">
          You are marking booking <strong>{reference}</strong> for <strong>{name}</strong> as completed.
        </p>;
      default:
        return <p className="mb-0">
          You are about to {actionType === "reopen" ? "re-open" : "cancel"} booking{" "}
          <strong>{reference}</strong> for <strong>{name}</strong>.
        </p>;
    }
  };
  if (!config) return null;
  return <Dialog
    open={Boolean(content.open)} fullWidth maxWidth={false} onClose={handleClose}
    aria-labelledby="package-booking-dialog-title"
    PaperProps={{
      sx: {
        borderRadius: "16px",
        width: content.width || 625
      }
    }}
  >
    <DialogTitle id="package-booking-dialog-title" className="border-bottom p-4 pe-5">
        {content.title || config.title}
    </DialogTitle>
    <IconButton
      disabled={loading} aria-label="Close dialog" onClick={handleClose}
      sx={{
        position: "absolute",
        right: 16,
        top: 16
      }}
    >
      <i className="ri-close-large-line" />
    </IconButton>
    <DialogContent className="admin-form p-4">
      <Stack component="form" spacing={3} noValidate onSubmit={handleSubmit}>
          {renderMessage()}
          {config.reasonKey && <Stack component="fieldset" spacing={3} className="admin-form-fieldset" disabled={loading}>
            {actionType === "reschedule" && <>
          <DatePicker
            title="New Appointment Date" required disabled={loading} name="appointment_date"
            values={values.appointment_date || ""} setFormValues={setValues}
          />
          <CustomTimePicker
            title="New Appointment Time" required ampm disabled={loading}
            values={values.appointment_time || ""}
            onChange={time => setValues(current => ({
                  ...current,
                  appointment_time: time?.isValid() ? time.format("HH:mm:ss") : ""
            }))}
          />
            </>}
            {config.reasonKey && <>
          <CustomSelect
            title={config.reasonLabel} required={actionType === "cancel"}
            values={values[config.reasonKey] || ""} data={reasons} placeholder="Select reason"
            disabled={loading} onChange={change(config.reasonKey)}
          />
          <CustomTextField
            title="Internal Note" values={values.internal_note || ""} placeholder="Optional note" multiline
            rows={5} disabled={loading} onChange={change("internal_note")}
          />
            </>}
        </Stack>}
        <Stack direction="row" justifyContent="flex-end" flexWrap="wrap" gap={1.5}>
          <Button color="light" type="button" disabled={loading} onClick={handleClose}>
              Cancel
          </Button>
          <Button
            color={config.color} type="submit" disabled={loading}
            className={isDelete ? "btn-package-delete" : undefined}
          >
              {config.buttonTitle}
          </Button>
        </Stack>
      </Stack>
    </DialogContent>
    </Dialog>;
}
